import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Play, Pause, Shuffle, X } from "lucide-react";
import logo from "@/assets/logo.png";
import PlayDelayModal from "@/components/PlayDelayModal";

import { BACKEND_URL, API_URL } from "@/config";

const API = API_URL;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [gymnasts, setGymnasts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playingId, setPlayingId] = useState(null);
  const [selectedGymnast, setSelectedGymnast] = useState(null);
  const audioRef = useRef(null);
  const [refreshClicks, setRefreshClicks] = useState(0);
  const [refreshTimer, setRefreshTimer] = useState(null);
  const [defaultBackgroundVolume, setDefaultBackgroundVolume] = useState(20);
  const [defaultFloorVolume, setDefaultFloorVolume] = useState(35);
  const [randomOrder, setRandomOrder] = useState(null); // { gymnastId: orderNumber }
  const [isShuffled, setIsShuffled] = useState(false);

  useEffect(() => {
    fetchData();
  }, [categoryId]);

  const fetchData = async () => {
    try {
      const [gymnastsRes, categoriesRes, volumeRes] = await Promise.all([
        axios.get(`${API}/gymnasts/by-category/${categoryId}`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/settings/volumes`)
      ]);
      setGymnasts(Array.isArray(gymnastsRes.data) ? gymnastsRes.data : []);
      const categoriesData = Array.isArray(categoriesRes.data) ? categoriesRes.data : [];
      const cat = categoriesData.find(c => c.id === categoryId);
      setCategory(cat);
      
      // Set volume defaults from settings
      if (volumeRes.data) {
        setDefaultBackgroundVolume(volumeRes.data.default_background_volume || 20);
        setDefaultFloorVolume(volumeRes.data.default_floor_volume || 35);
      }
    } catch (error) {
      console.error("Error fetching gymnasts:", error);
      toast.error("Failed to load gymnasts");
      // Ensure arrays are set even on error
      setGymnasts([]);
    } finally {
      setLoading(false);
    }
  };

  const fadeSpotifyVolume = async (targetVolume, duration = 1000) => {
    // Access Spotify player if available
    if (window.spotifyPlayer) {
      const player = window.spotifyPlayer;
      const steps = 20;
      const stepDuration = duration / steps;
      
      try {
        const currentVolume = await player.getVolume();
        const volumeStep = (targetVolume - currentVolume) / steps;
        
        for (let i = 0; i < steps; i++) {
          const newVolume = currentVolume + (volumeStep * (i + 1));
          await player.setVolume(Math.max(0, Math.min(1, newVolume)));
          await new Promise(resolve => setTimeout(resolve, stepDuration));
        }
      } catch (err) {
        console.log('Error fading Spotify volume:', err);
      }
    }
  };

  const pauseSpotify = async () => {
    if (window.spotifyPlayer) {
      try {
        await window.spotifyPlayer.pause();
        console.log('Spotify paused');
      } catch (err) {
        console.log('Error pausing Spotify:', err);
      }
    }
  };

  const resumeSpotify = async () => {
    if (window.spotifyPlayer) {
      try {
        // Get current state to see if there's a track loaded
        const state = await window.spotifyPlayer.getCurrentState();
        
        if (!state) {
          console.log('No Spotify track loaded - cannot resume');
          toast.info('Please select a background music playlist');
          return false;
        }
        
        // Resume playback
        await window.spotifyPlayer.resume();
        console.log('Spotify resumed successfully');
        return true;
      } catch (err) {
        console.error('Error resuming Spotify:', err);
        return false;
      }
    } else {
      console.log('Spotify player not available');
      return false;
    }
  };

  const playAudio = async () => {
    if (!selectedGymnast) return;

    try {
      console.log('=== STARTING MP3 PLAYBACK ===');
      
      // Store radio state if playing to resume later
      let wasRadioPlaying = false;
      let radioStation = null;
      if (window.currentMP3Audio && !window.currentMP3Audio.paused) {
        // Check if it's a radio stream (no duration = live stream)
        if (isNaN(window.currentMP3Audio.duration) || !isFinite(window.currentMP3Audio.duration)) {
          wasRadioPlaying = true;
          radioStation = window.currentRadioStation;
          console.log('Radio was playing, will resume after floor music');
        }
        // Pause current audio (radio or previous MP3)
        window.currentMP3Audio.pause();
        window.currentMP3Audio = null;
      }
      
      // Set flag to block Spotify from updating player
      window.mp3PlaybackActive = true;
      
      // Fade out and pause Spotify FIRST
      await fadeSpotifyVolume(0, 1000);
      await pauseSpotify();

      // Stop any currently playing audio
      if (audioRef.current) {
        audioRef.current.pause();
      }

      // Wait a tiny bit for Spotify to fully stop any pending updates
      await new Promise(resolve => setTimeout(resolve, 100));

      // IMMEDIATELY update unified player to show MP3 track (seamless transition)
      console.log('=== SWITCHING PLAYER TO MP3 ===', selectedGymnast.name);
      if (window.updatePlayerTrack) {
        window.updatePlayerTrack({
          name: selectedGymnast.name,
          artist: category?.name || 'Floor Music',
          image: null
        }, 'mp3');
      }

      // Play new audio
      const audioUrl = `${API}/gymnasts/${selectedGymnast.id}/audio`;
      console.log('Creating MP3 Audio object for URL:', audioUrl);
      const audio = new Audio(audioUrl);
      
      // Use gymnast's custom volume if set, otherwise use admin-defined floor music default
      const gymnastVolume = selectedGymnast.volume || defaultFloorVolume;
      audio.volume = gymnastVolume / 100;
      console.log(`MP3 volume set to ${gymnastVolume}%:`, audio.volume);
      
      // Update UnifiedPlayer volume to match
      if (window.updateVolumeSlider) {
        window.updateVolumeSlider(gymnastVolume);
      }
      
      // Add loading listeners
      audio.addEventListener('loadstart', () => {
        console.log('MP3 loading started...');
      });
      
      audio.addEventListener('canplay', () => {
        console.log('MP3 can play - audio ready');
      });
      
      audio.addEventListener('playing', () => {
        console.log('MP3 is now playing');
      });
      
      // Store audio globally
      window.currentMP3Audio = audio;
      console.log('MP3 Audio object stored in window.currentMP3Audio');
      
      // Handle play promise properly
      console.log('Attempting to play MP3...');
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.then(() => {
          console.log('MP3 play() promise resolved successfully');
          toast.success(`Playing ${selectedGymnast.name}`);
        }).catch(err => {
          // Only show error if it's a real error, not autoplay prevention
          if (err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
            console.error('Audio play error:', err);
            toast.error('Failed to play audio');
          } else {
            console.log('Autoplay prevented, user interaction may be required');
          }
        });
      }
      
      audio.onpause = () => {
        console.log('MP3 paused event fired');
        if (window.updatePlayerPaused) window.updatePlayerPaused(true);
      };
      
      audio.onplay = () => {
        console.log('MP3 play event fired');
        if (window.updatePlayerPaused) window.updatePlayerPaused(false);
      };
      
      audio.onplaying = () => {
        console.log('MP3 actually playing now');
        if (window.updatePlayerPaused) window.updatePlayerPaused(false);
      };
      
      // Don't show error toast for normal audio errors - log only
      audio.onerror = (e) => {
        console.log('Audio error event (may be transient):', e);
        // Only show error if audio actually failed to load
        if (audio.error && audio.error.code === audio.error.MEDIA_ERR_SRC_NOT_SUPPORTED) {
          toast.error('Audio file format not supported');
        }
      };
      
      audio.onended = async () => {
        console.log('=== MP3 ENDED - RESUMING BACKGROUND MUSIC ===');
        setPlayingId(null);
        if (window.clearPlayerTrack) window.clearPlayerTrack();
        window.currentMP3Audio = null;
        
        // Clear MP3 active flag - allow Spotify to update player again
        window.mp3PlaybackActive = false;
        
        // Check if radio was playing before - resume it
        if (wasRadioPlaying && radioStation) {
          console.log('Resuming radio after floor music ended');
          setTimeout(() => {
            // Re-play the radio station
            if (window.playRadioStation) {
              window.playRadioStation(radioStation);
            }
          }, 1000);
          return;
        }
        
        // Otherwise, wait 2 seconds then resume and fade Spotify back in
        setTimeout(async () => {
          console.log('Resuming background music...');
          
          const resumed = await resumeSpotify();
          
          if (resumed) {
            // Successfully resumed, now fade volume back to background default
            const bgVolume = defaultBackgroundVolume / 100;
            await fadeSpotifyVolume(bgVolume, 2000);
            console.log(`Background music resumed at ${defaultBackgroundVolume}%`);
            toast.success('Background music resumed', { duration: 2000 });
            
            // Update volume slider
            if (window.updateVolumeSlider) {
              window.updateVolumeSlider(defaultBackgroundVolume);
            }
          } else {
            // Failed to resume, try again
            console.log('First resume failed, retrying...');
            setTimeout(async () => {
              const retried = await resumeSpotify();
              if (retried) {
                const bgVolume = defaultBackgroundVolume / 100;
                await fadeSpotifyVolume(bgVolume, 1000);
                console.log(`Background music resumed at ${defaultBackgroundVolume}% (retry successful)`);
                toast.success('Background music resumed');
                
                // Update volume slider
                if (window.updateVolumeSlider) {
                  window.updateVolumeSlider(defaultBackgroundVolume);
                }
              } else {
                console.error('Resume failed completely');
                toast.error('Please click a background music playlist to resume');
              }
            }, 1000);
          }
        }, 2000);
      };
      
      audioRef.current = audio;
      setPlayingId(selectedGymnast.id);
      setSelectedGymnast(null); // Close modal
    } catch (error) {
      console.error('Error playing audio:', error);
      toast.error('Failed to play audio');
      setSelectedGymnast(null);
      
      // Clear MP3 active flag on error
      window.mp3PlaybackActive = false;
      
      // Resume Spotify if there was an error
      await resumeSpotify();
      const bgVolume = defaultBackgroundVolume / 100;
      await fadeSpotifyVolume(bgVolume, 1000);
    }
  };

  const handleGymnastClick = (gymnast) => {
    if (playingId === gymnast.id) {
      // Pause currently playing
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setPlayingId(null);
    } else if (gymnast.hasAudio) {
      // Show delay modal
      setSelectedGymnast(gymnast);
    }
  };

  const shuffleGymnasts = () => {
    if (gymnasts.length === 0) return;
    
    // Create a shuffled array of indices
    const indices = gymnasts.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    
    // Create order map: gymnastId -> order number (1-based)
    const orderMap = {};
    indices.forEach((originalIndex, newPosition) => {
      orderMap[gymnasts[originalIndex].id] = newPosition + 1;
    });
    
    setRandomOrder(orderMap);
    setIsShuffled(true);
    toast.success('Order randomized!');
  };

  const clearShuffle = () => {
    setRandomOrder(null);
    setIsShuffled(false);
  };

  // Sort gymnasts by their random order if shuffled
  const displayGymnasts = isShuffled && randomOrder
    ? [...gymnasts].sort((a, b) => (randomOrder[a.id] || 0) - (randomOrder[b.id] || 0))
    : gymnasts;

  const handleRefreshClick = () => {
    const newCount = refreshClicks + 1;
    setRefreshClicks(newCount);

    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }

    if (newCount === 1) {
      toast.info('Click 2 more times to refresh');
    } else if (newCount === 2) {
      toast.info('Click 1 more time to refresh');
    } else if (newCount >= 3) {
      toast.success('Refreshing...');
      setTimeout(() => window.location.reload(), 500);
      return;
    }

    const timer = setTimeout(() => {
      setRefreshClicks(0);
    }, 5000);
    setRefreshTimer(timer);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {selectedGymnast && (
        <PlayDelayModal
          gymnastName={selectedGymnast.name}
          onPlay={playAudio}
          onClose={() => setSelectedGymnast(null)}
        />
      )}
      
      <div className="min-h-screen p-4 pb-24" data-testid="category-page">
      {/* Discreet Refresh Button - requires 3 clicks in 5 seconds */}
      <button
        onClick={handleRefreshClick}
        className={`fixed top-4 right-4 bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors z-40 ${
          refreshClicks > 0 ? 'opacity-100 ring-2 ring-white/50' : 'opacity-50 hover:opacity-100'
        }`}
        title="Click 3 times to refresh"
        data-testid="refresh-button"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
        </svg>
        {refreshClicks > 0 && (
          <span className="absolute -top-1 -right-1 bg-[#ff3131] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
            {refreshClicks}
          </span>
        )}
      </button>
      
      {/* Header */}
      <div className="flex flex-col items-center mb-4">
        <img src={logo} alt="Dynamo Gymnastics" className="logo mb-2" data-testid="category-logo" />
        <button
          onClick={() => navigate('/')}
          className="btn-secondary flex items-center gap-2 mb-3"
          data-testid="back-button"
        >
          <ArrowLeft size={18} />
          Back to Home
        </button>
        {category && (
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold" data-testid="category-name">
              {category.name}
            </h1>
            {gymnasts.length > 1 && (
              <div className="flex gap-2">
                <button
                  onClick={shuffleGymnasts}
                  className="bg-white/20 hover:bg-white/30 p-2 rounded-full transition-colors"
                  title="Randomize order"
                  data-testid="shuffle-button"
                >
                  <Shuffle size={20} />
                </button>
                {isShuffled && (
                  <button
                    onClick={clearShuffle}
                    className="bg-[#ff3131]/80 hover:bg-[#ff3131] p-2 rounded-full transition-colors"
                    title="Clear random order"
                    data-testid="clear-shuffle-button"
                  >
                    <X size={20} />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Gymnasts Grid */}
      <div className="max-w-5xl mx-auto">
        <div className="glass-card" data-testid="gymnasts-section">
          <h2 className="text-xl font-bold mb-3 text-center" data-testid="gymnasts-title">
            Select Gymnast
          </h2>
          {(gymnasts || []).length === 0 ? (
            <p className="text-center text-lg opacity-80" data-testid="no-gymnasts-message">
              No gymnasts in this category yet
            </p>
          ) : (
            <>
              {isShuffled && (
                <div className="mb-4 p-3 bg-white/10 rounded-lg text-center">
                  <p className="text-sm font-medium opacity-90">
                    Random order set! Numbers show performance order.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {(displayGymnasts || []).map((gymnast) => (
                  <button
                    key={gymnast.id}
                    className="btn-primary flex items-center justify-center gap-3 relative"
                    onClick={() => handleGymnastClick(gymnast)}
                    disabled={!gymnast.hasAudio}
                    style={{ opacity: gymnast.hasAudio ? 1 : 0.5 }}
                    data-testid={`gymnast-${gymnast.id}`}
                  >
                    {/* Order number badge */}
                    {isShuffled && randomOrder && randomOrder[gymnast.id] && (
                      <span 
                        className="absolute -top-2 -left-2 bg-white text-[#1656A4] w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-lg"
                        data-testid={`order-badge-${gymnast.id}`}
                      >
                        {randomOrder[gymnast.id]}
                      </span>
                    )}
                    {playingId === gymnast.id ? (
                      <Pause size={24} data-testid={`pause-icon-${gymnast.id}`} />
                    ) : (
                      <Play size={24} data-testid={`play-icon-${gymnast.id}`} />
                    )}
                    {gymnast.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
}
